// release 87 (t3-speed, agents/t3-speed-report.md): the Sentry browser SDK (@sentry/nextjs, about 170 KB of JavaScript, 52 KB gzipped) is
// no longer part of every page's first JavaScript. instrumentation-client.ts loads it with loadSentry() once the page has loaded (the window
// load event, at most 10 s after the page starts), so it no longer competes with the page's HTML, stylesheet, fonts and photos on a phone.
// The options below are exactly the ones instrumentation-client.ts passed to Sentry.init before (scripts/test-t3-speed.cjs pins them).
// Tracing still measures the whole page load: the SDK starts its pageload span at the navigation's time origin and reads the page's
// paint, layout-shift and resource timings from the browser's buffered performance entries. Errors thrown before the SDK loads are kept by
// instrumentation-client.ts and sent once it is in.
import type { BrowserOptions, ErrorEvent } from '@sentry/nextjs';

type SentryModule = typeof import('@/lib/sentrySdk');

const dsn = 'https://1f61895ade9a7be0c3c6646ab50607c4@o4508796672671744.ingest.us.sentry.io/4510195000279040';

function isFirebaseIndexedDbLifecycleNoise(event: ErrorEvent): boolean {
  const currentErrorText = [
    event.message,
    event.exception?.values?.map((value) => `${value.type || ''}: ${value.value || ''}`).join('\n'),
  ].filter(Boolean).join('\n');

  return /^(Error:\s*)?UnknownError: Database deleted by request of the user$/m.test(currentErrorText)
    || /^Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing\.$/m.test(currentErrorText)
    || /^(FirebaseError:\s*)?Firebase: Error thrown when (reading from|writing to|opening) IndexedDB\./m.test(currentErrorText);
}

export const SENTRY_OPTIONS: BrowserOptions = {
  dsn,
  environment: 'production',
  tracesSampleRate: 0.1,
  enabled: typeof window !== 'undefined' && process.env.NODE_ENV === 'production',
  sendDefaultPii: false,
  ignoreErrors: [
    /^Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing\.$/,
    /^UnknownError: Database deleted by request of the user$/,
    /^Error: UnknownError: Database deleted by request of the user$/,
    /^Firebase: Error thrown when opening IndexedDB\. Original error: The operation was aborted\.\. \(app\/idb-open\)\.$/,
  ],
  beforeSend(event) {
    if (isFirebaseIndexedDbLifecycleNoise(event)) {
      return null;
    }

    return event;
  },
};

let loading: Promise<SentryModule | null> | null = null;
let loaded: SentryModule | null = null;

/** Loads the SDK and initialises it once (later calls share the first load). A failed load resolves to null and is retried on the next call. */
export function loadSentry(): Promise<SentryModule | null> {
  loading ??= import('@/lib/sentrySdk').then(
    (Sentry) => {
      Sentry.init(SENTRY_OPTIONS);
      loaded = Sentry;
      return Sentry;
    },
    () => {
      loading = null;
      return null;
    },
  );
  return loading;
}

/** The initialised SDK, or null while it has not loaded yet. */
export function loadedSentry(): SentryModule | null {
  return loaded;
}
